import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type {
  DemandSummaryResponse,
  DemandSummaryOption,
} from "./reports-demand-summary";

export type SettlementSummaryFilters = {
  startDate: string;
  endDate: string;
  roleId?: string;
  page: number;
  pageSize: number;
};
export type SettlementSummaryTeam = {
  teamId: string | null;
  label: string;
  awaitingMatters: number;
  awaitingRecords: number;
  awaitingAmount: string | null;
  issueMatters: number;
  issueRecords: number;
  issueAmount: string | null;
};
export type SettlementSummaryWorkload = {
  userId: string;
  label: string;
  matters: number;
  liens: number;
  averageDaysSinceAcceptance: string | null;
};
export type SettlementSummaryMatter = {
  matterId: string;
  matterName: string | null;
  closedDate: string;
  teams: string[];
  acceptedRecords: number;
  acceptedAmount: string | null;
  expectedFees: string | null;
  expenseCost: string | null;
  trustDeposits: string | null;
  demands: number | null;
  acceptedPerDemandPercent: string | null;
  daysToAcceptance: string | null;
  daysDemandToAcceptance: string | null;
  daysAcceptanceToDeposit: string | null;
  daysDepositToClose: string | null;
  daysToClose: number | null;
  expectedFeesPer30Days: string | null;
};
export type SettlementSummaryClosure = { date: string; count: number };
export type SettlementSummaryResponse = {
  data: {
    teams: SettlementSummaryTeam[];
    totals: SettlementSummaryTeam;
    workload: SettlementSummaryWorkload[];
    closedMatters: SettlementSummaryMatter[];
    totalClosedMatters: number;
    closures: SettlementSummaryClosure[];
    roles: DemandSummaryOption[];
    pinnedRoleId?: string | null;
    selectedRoleId?: string | null;
    firmDateModified?: string | null;
    canManagePinnedRole?: boolean;
    freshness: DemandSummaryResponse["data"]["freshness"];
  };
};
export async function retrieveSettlementSummary(
  filters: SettlementSummaryFilters,
  signal?: AbortSignal,
) {
  const response =
    await authenticatedFastApiAxiosInstance.post<SettlementSummaryResponse>(
      "reports/v2/settlement-summary/query",
      filters,
      { signal },
    );
  return response.data;
}

export function hasValidSettlementDates(filters: SettlementSummaryFilters) {
  const days =
    (Date.parse(`${filters.endDate}T00:00:00Z`) -
      Date.parse(`${filters.startDate}T00:00:00Z`)) /
    86_400_000;
  return days >= 0 && days <= 3660;
}

export type SettlementReportDefaultRoleInput = {
  roleId: string | null;
  expectedPinnedRoleId: string | null;
  firmDateModified: string | null;
};
export async function updateSettlementReportDefaultRole(
  input: SettlementReportDefaultRoleInput,
) {
  const response = await authenticatedFastApiAxiosInstance.patch<{
    data: { pinnedRoleId: string | null; firmDateModified: string | null };
  }>("reports/v2/settlement-summary/default-role", input);
  return response.data;
}
